import React, { useState, useEffect } from 'react';

import { useAuth } from '../contexts/AuthContext';
import { propertyApi, type PropertyData } from '../services';
import { useToast } from '../contexts/ToastContext';
import HouseIcon from '../assets/house.svg';
import EuroIcon from '../assets/euro.svg';
import CashIcon from '../assets/cash.svg';
import GraphIcon from '../assets/graph-icon.svg';
import './DashboardPage.css';
import PolygonUp from '../components/PolygonUp';
import PolygonDown from '../components/PolygonDown';
import { LineChart } from '@mui/x-charts';

const DashboardPage: React.FC = () => {
  const {} = useAuth();

  const { showError } = useToast();
  const [properties, setProperties] = useState<PropertyData[]>([]);

  // Helper function to normalize property data from API (convert string numbers to actual numbers)
  const normalizeProperty = (prop: PropertyData): PropertyData => {
    return {
      ...prop,
      kaufpreis:
        prop.kaufpreis !== undefined && prop.kaufpreis !== null
          ? typeof prop.kaufpreis === 'string'
            ? parseFloat(prop.kaufpreis)
            : prop.kaufpreis
          : undefined,
      currentValue:
        prop.currentValue !== undefined && prop.currentValue !== null
          ? typeof prop.currentValue === 'string'
            ? parseFloat(prop.currentValue)
            : prop.currentValue
          : undefined,
      gain: typeof prop.gain === 'string' ? parseFloat(prop.gain) : prop.gain,
      gainPercentage:
        typeof prop.gainPercentage === 'string'
          ? parseFloat(prop.gainPercentage)
          : prop.gainPercentage,
    };
  };

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const data = await propertyApi.getAll();
        // Normalize all properties to ensure numeric fields are numbers
        const normalizedData = data.map(normalizeProperty);
        setProperties(normalizedData);
      } catch (error: any) {
        showError(error.message || 'Failed to load properties');
      } finally {
      }
    };

    fetchProperties();
  }, []);

  // Calculate statistics from properties
  const totalProperties = properties.length;
  const portfolioValue = properties.reduce(
    (sum, prop) => sum + (prop.currentValue || 0),
    0,
  );
  const totalGain = properties.reduce((sum, prop) => sum + prop.gain, 0);

  // Calculate gain percentage for portfolio
  const totalPurchasePrice = properties.reduce(
    (sum, prop) => sum + (prop.kaufpreis || 0),
    0,
  );
  const portfolioGainPercentage =
    totalPurchasePrice > 0 ? (totalGain / totalPurchasePrice) * 100 : 0;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatGain = (gain: number) => {
    const sign = gain >= 0 ? '+' : '-';
    return `${sign}${formatCurrency(gain)}`;
  };

  return (
    <div className="dashboard-page">
      <header className="dashboard-header">
        <h2>Portfolioübersicht</h2>
      </header>

      <main className="dashboard-content">
        <div className="dashboard-left">
          <div className="card-header-row">
            <div className="card-header">Gesamtwert & Wertentwicklung</div>
            <div className="card-icon">
              <img src={GraphIcon} alt="" className="graph-icon" />
            </div>
          </div>

          <div className="card-content-row-split">
            <div className="card-split-content">
              <div className="card-subtitle">Geschätzter Gesamtwert</div>
              <div className="card-value">
                <div className="card-value-split">
                  <div className="card-value-split-content">
                    {formatCurrency(portfolioValue)}
                  </div>
                  <div className="card-value-split-content">
                    <div className="card-value-gain">
                      {portfolioGainPercentage >= 0 ? (
                        <PolygonUp />
                      ) : (
                        <PolygonDown />
                      )}
                      {portfolioGainPercentage.toFixed(2)} %
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="card-split-content">
              <div className="card-subtitle">Geschätzte Wertentwicklung</div>
              <div
                className={`card-value ${totalGain >= 0 ? 'card-value-positive' : 'card-value-negative'}`}
              >
                {formatGain(totalGain)}
              </div>
            </div>
          </div>

          <div className="portfolio-development-graph">
            <div className="portfolio-development-timeframe">
              <div className="portfolio-development-timeframe-selector">
                1 Monat
              </div>
              <div className="portfolio-development-timeframe-selector">
                6 Monate
              </div>
              <div className="portfolio-development-timeframe-selector">
                1 Jahr
              </div>
              <div className="portfolio-development-timeframe-selector-active">
                Max.
              </div>
            </div>
            <div className="portfolio-development-graph-content">
              <LineChart
                series={[
                  {
                    curve: 'linear',
                    data: [1, 1.1, 2, 4, 3, 9.3],
                    color: '#4FFF84',
                  },
                ]}
              />
            </div>
          </div>
        </div>
        <div className="dashboard-right">
          <div className="dashboard-card-row">
            <div className="dashboard-card dashboard-card-2">
              <div className="card-header-row">
                <div className="card-header">Gesamtzahl der Immobilien</div>
                <div className="card-icon">
                  <img src={HouseIcon} alt="" className="house-icon" />
                </div>
              </div>
              <div className="card-content">
                <div className="card-subtitle">Immobilien im Portfolio</div>
                <div className="card-value">{totalProperties}</div>
              </div>
            </div>

            <div className="dashboard-card dashboard-card-2">
              <div className="card-header-row">
                <div className="card-header">Gesamtrendite</div>
                <div className="card-icon">
                  <img src={EuroIcon} alt="" className="euro-icon" />
                </div>
              </div>
              <div className="card-content">
                <div className="card-subtitle">
                  Wertsteigerung und Mieteinnahmen
                </div>
                <div
                  className={`card-value ${totalGain >= 0 ? 'card-value-positive' : 'card-value-negative'}`}
                >
                  {formatGain(totalGain)}
                </div>
              </div>
            </div>
          </div>

          <div className="dashboard-card-row">
            <div className="dashboard-card dashboard-card-1">
              <div className="card-header-row">
                <div className="card-header">Finanzen</div>
                <div className="card-icon">
                  <img src={CashIcon} alt="" className="cash-icon" />
                </div>
              </div>
              <div className="card-content-row-split">
                <div className="card-split-content">
                  <div className="card-subtitle">Eigenkapital</div>
                  <div className="card-value">450.000 €</div>
                </div>
                <div className="card-split-content">
                  <div className="card-subtitle">Fremdkapital</div>
                  <div className="card-value">450.000 €</div>
                </div>
              </div>
              <div className="card-content-row-split">
                <div className="card-split-content">
                  <div className="card-subtitle">Monatliche Fixkosten</div>
                  <div className="card-value">1.484,95 €</div>
                </div>
                <div className="card-split-content">
                  <div className="card-subtitle">
                    Empfohlene Instandhaltungsrücklagen
                  </div>
                  <div className="card-value">190.000 €</div>
                </div>
              </div>
            </div>
          </div>

          <div className="dashboard-card-row">
            <div className="dashboard-card dashboard-card-1">
              <div className="card-header-row">
                <div className="card-header">Mieteinnahmen</div>
                <div className="card-icon">
                  <img src={EuroIcon} alt="" className="euro-icon" />
                </div>
              </div>
              <div className="card-content-row-split">
                <div className="card-split-content">
                  <div className="card-subtitle">
                    Ø monatliche Nettomieteinnahmen
                  </div>
                  <div className="card-value">1.113 €</div>
                </div>
                <div className="card-split-content">
                  <div className="card-subtitle">
                    Ø Gesamte Nettomieteinnahmen
                  </div>
                  <div className="card-value">133.500 €</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DashboardPage;
